import styled from '@emotion/styled'
import { ChevronDown } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import useOnClickOutside from 'app/utils/useOnClickOutside'
import { AnimatePresence, motion } from 'framer-motion'
import React, { memo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { LanguageItem, Props as LanguageItemProps } from './LanguageItem'

export interface Props {
  isSticky?: boolean
  languagesList: LanguageItemProps[]
  languagePrefix: string
}

export const Languages = memo(function Languages({
  isSticky,
  languagesList,
  languagePrefix,
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  })

  const ref = useRef<any>()
  const [visibility, setVisibility] = useState(false)

  useOnClickOutside(ref, () => setVisibility(false))

  return (
    <Container
      className={`${isAnimObjectVisible ? 'visible' : ''}${
        isSticky ? ' is-sticky' : ''
      }`}
      ref={animObjectRef}
    >
      <Wrapper ref={ref}>
        <LanguageActive
          className={visibility ? 'open' : undefined}
          dial={4}
          onClick={() =>
            visibility ? setVisibility(false) : setVisibility(true)
          }
          row
        >
          {languagesList
            .filter((t) => t.languagePrefix === languagePrefix)
            .map((item, index) => (
              <LanguageItem
                currentPrefix={languagePrefix}
                key={index}
                {...item}
              />
            ))}

          {languagesList.filter((t) => t.languagePrefix !== languagePrefix)
            .length > 0 ? (
            <ChevronDown />
          ) : null}
        </LanguageActive>

        {languagesList.filter((t) => t.languagePrefix !== languagePrefix)
          .length > 0 ? (
          <AnimatePresence mode="wait">
            {visibility && (
              <OthersLanguages
                initial={{ height: 0 }}
                animate={{
                  height: 'auto',
                }}
                exit={{ height: 0 }}
                key="languages"
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <Inner>
                  {languagesList
                    .sort((a: any, b: any) => a.sort - b.sort)
                    .filter((t) => t.languagePrefix !== languagePrefix)
                    .map((item, index) => (
                      <LanguageItem
                        currentPrefix={languagePrefix}
                        key={index}
                        variant="invert"
                        {...item}
                      />
                    ))}
                </Inner>
              </OthersLanguages>
            )}
          </AnimatePresence>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Wrapper = styled.div``

const LanguageActive = styled(FlexBox)`
  cursor: pointer;
  &.open {
    svg {
      transform: scaleY(-1);
    }
  }

  svg {
    width: 0.4375rem;
    height: auto;
    fill: ${({ theme }) => theme.colors.variants.neutralLight3};
    margin-left: 0.375rem;
    transform: translateY(0.0625rem);
    transition: 200ms;
  }
`

const OthersLanguages = styled(motion.div)`
  width: 2.5rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight1};
  overflow: hidden;
  text-align: center;
  position: absolute;
  top: 2.25rem;
  left: -0.375rem;
`

const Inner = styled.div`
  padding: 0.75rem;
`

const Container = styled.div`
  opacity: 0;
  margin-right: 1.5rem;
  transform: translateY(-2.5rem);
  position: relative;
  transition: 0.8s 0.2s ease-in-out;
  &.is-sticky {
    ${LanguageActive} {
      a {
        color: ${({ theme }) => theme.colors.variants.neutralDark1};
      }
      svg {
        fill: ${({ theme }) => theme.colors.variants.neutralDark1};
      }
    }
  }
  &.visible {
    opacity: 1;
    transform: none;
  }
`
